import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import ExportDialog from '@/components/shared/ExportDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Search, MoreHorizontal, Edit, Eye, Trash2, CheckCircle, XCircle, Ban, RotateCcw, CreditCard, Users, UserCheck, UserX, Clock, Shield, Download } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { membersApi, adminMemberTypesApi, playingLevelsApi, playingPositionsApi } from '@/lib/api';
import { exportToCSV, prepareMemberDataForExport } from '@/lib/exportUtils';
import MemberFormTemplate from '@/components/shared/MemberFormTemplate';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Member {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  membership_type?: string;
  status: string;
  created_at: string;
  playing_level?: string | {
    id: number;
    name: string;
    description: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
    created_at?: string;
    updated_at?: string;
  };
  jersey_number?: number;
  membership_expires_at?: string;
  photo?: string;
  photo_url?: string;
  dob?: string;
  nationality?: string;
  id_passport?: string;
  residential_address?: string;
  occupation?: string;
  employer?: string;
  emergency_contact_name?: string;
  emergency_contact_relationship?: string;
  emergency_contact_phone?: string;
  playing_level_id?: number;
  playing_position_id?: number;
  medical_conditions?: string;
  allergies?: string;
  insurance_provider?: string;
  member_type?: {
    id: number;
    name: string;
    description: string;
    monthly_fee: string;
    is_active?: boolean;
    created_at?: string;
    updated_at?: string;
  };
  playing_position?: {
    id: number;
    name: string;
    description: string;
    abbreviation: string;
    is_active?: boolean;
    sort_order?: number;
    created_at?: string;
    updated_at?: string;
  };
}

interface MemberStats {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  disabled: number;
  expiring: number;
}

interface MemberType {
  id: number;
  name: string;
  description: string;
  monthly_fee: string;
}

interface PlayingLevel {
  id: number;
  name: string;
  description: string;
  color?: string;
  is_active: boolean;
  sort_order: number;
}

interface PlayingPosition {
  id: number;
  name: string;
  description: string;
  abbreviation: string;
  is_active: boolean;
  sort_order: number;
}

const AdminMembers = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [stats, setStats] = useState<MemberStats>({ total: 0, active: 0, pending: 0, suspended: 0, disabled: 0, expiring: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  
  // Form states
  const [isFormDataLoading, setIsFormDataLoading] = useState(false);
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [playingLevels, setPlayingLevels] = useState<PlayingLevel[]>([]);
  const [playingPositions, setPlayingPositions] = useState<PlayingPosition[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', dob: '', nationality: '', id_passport: '',
    residential_address: '', occupation: '', employer: '', emergency_contact_name: '', emergency_contact_relationship: '',
    emergency_contact_phone: '', membership_type: '', playing_level: '', playing_level_id: '', playing_position_id: '',
    jersey_number: '', medical_conditions: '', allergies: '', insurance_provider: ''
  });
  
  // Action dialog state
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionMember, setActionMember] = useState<Member | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'suspend' | 'disable' | 'reactivate'>('approve');
  
  // Photo management states
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoMember, setPhotoMember] = useState<Member | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // Bulk actions states
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'approve' | 'suspend' | 'disable' | 'reactivate'>('approve');
  
  // Export states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Fetch members
  const fetchMembers = async () => {
    try {
      setIsLoading(true);
      
      // Get all members with search and status filters
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const response: any = await membersApi.getAll(params);
      
      let membersData: Member[] = [];
      if (Array.isArray(response)) {
        membersData = response;
      } else if (response?.data && Array.isArray(response.data)) {
        membersData = response.data;
      } else if (response?.members && Array.isArray(response.members)) {
        membersData = response.members;
      }
      
      setMembers(membersData);
      setStats(response?.stats || calculateStats(membersData));
      
    } catch (error: any) {
      console.error('Error fetching members:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to fetch members';
      toast({ 
        title: "Error", 
        description: errorMessage, 
        variant: "destructive" 
      });
      setMembers([]);
      setStats({ total: 0, active: 0, pending: 0, suspended: 0, disabled: 0, expiring: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStats = (membersArray: Member[]): MemberStats => {
    if (!Array.isArray(membersArray)) {
      return { total: 0, active: 0, pending: 0, suspended: 0, disabled: 0, expiring: 0 };
    }
    
    return {
      total: membersArray.length,
      active: membersArray.filter(m => m.status === 'active').length,
      pending: membersArray.filter(m => m.status === 'pending').length,
      suspended: membersArray.filter(m => m.status === 'suspended').length,
      disabled: membersArray.filter(m => m.status === 'disabled').length,
      expiring: membersArray.filter(m => {
        if (!m.membership_expires_at) return false;
        const expiryDate = new Date(m.membership_expires_at);
        const today = new Date();
        const daysDifference = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
        return daysDifference <= 30 && daysDifference > 0; // Expiring within 30 days
      }).length
    };
  };

  // Fetch form data
  const fetchFormData = async () => {
    try {
      setIsFormDataLoading(true);
      const [memberTypesResponse, playingLevelsResponse, playingPositionsResponse] = await Promise.all([
        adminMemberTypesApi.getAll().catch(() => []),
        playingLevelsApi.getAll().catch(() => []),
        playingPositionsApi.getAll().catch(() => [])
      ]);
      
      const types = Array.isArray(memberTypesResponse) ? memberTypesResponse : ((memberTypesResponse as any)?.data || []);
      const levels = Array.isArray(playingLevelsResponse) ? playingLevelsResponse : ((playingLevelsResponse as any)?.data || []);
      const positions = Array.isArray(playingPositionsResponse) ? playingPositionsResponse : ((playingPositionsResponse as any)?.data || []);
      
      setMemberTypes(types);
      setPlayingLevels(levels.filter((l: any) => l.is_active !== false).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)));
      setPlayingPositions(positions.filter((p: any) => p.is_active !== false).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)));
    } catch (error) {
      console.error('Error fetching form data:', error);
      toast({ title: "Error", description: "Failed to fetch form data", variant: "destructive" });
    } finally {
      setIsFormDataLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '', last_name: '', email: '', phone: '', dob: '', nationality: '', id_passport: '',
      residential_address: '', occupation: '', employer: '', emergency_contact_name: '', emergency_contact_relationship: '',
      emergency_contact_phone: '', membership_type: '', playing_level: '', playing_level_id: '', playing_position_id: '',
      jersey_number: '', medical_conditions: '', allergies: '', insurance_provider: ''
    });
  };

  const handleAddMember = async () => {
    try {
      setIsSubmitting(true);
      const memberData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        dob: formData.dob || null,
        nationality: formData.nationality || null,
        id_passport: formData.id_passport || null,
        residential_address: formData.residential_address || null,
        occupation: formData.occupation || null,
        employer: formData.employer || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_relationship: formData.emergency_contact_relationship || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        membership_type: formData.membership_type,
        playing_level: formData.playing_level,
        playing_level_id: formData.playing_level_id ? parseInt(formData.playing_level_id) : null,
        playing_position_id: formData.playing_position_id ? parseInt(formData.playing_position_id) : null,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
        medical_conditions: formData.medical_conditions || null,
        allergies: formData.allergies || null,
        insurance_provider: formData.insurance_provider || null
      };
      
      await membersApi.create(memberData);
      toast({ title: "Success", description: "Member added successfully" });
      setIsAddDialogOpen(false);
      resetForm();
      fetchMembers();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || Object.values(error.response?.data?.errors || {}).flat().join(', ') || 'Failed to add member';
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditMember = async () => {
    if (!selectedMember) return;
    try {
      setIsSubmitting(true);
      const memberData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        dob: formData.dob || null,
        nationality: formData.nationality || null,
        id_passport: formData.id_passport || null,
        residential_address: formData.residential_address || null,
        occupation: formData.occupation || null,
        employer: formData.employer || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_relationship: formData.emergency_contact_relationship || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        membership_type: formData.membership_type,
        playing_level: formData.playing_level,
        playing_level_id: formData.playing_level_id ? parseInt(formData.playing_level_id) : null,
        playing_position_id: formData.playing_position_id ? parseInt(formData.playing_position_id) : null,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
        medical_conditions: formData.medical_conditions || null,
        allergies: formData.allergies || null,
        insurance_provider: formData.insurance_provider || null
      };
      
      await membersApi.update(selectedMember.id, memberData);
      toast({ title: "Success", description: "Member updated successfully" });
      setIsEditDialogOpen(false);
      setSelectedMember(null);
      resetForm();
      fetchMembers();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || Object.values(error.response?.data?.errors || {}).flat().join(', ') || 'Failed to update member';
      toast({ title: "Error", description: errorMsg, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openAddDialog = async () => {
    await fetchFormData();
    resetForm();
    setIsAddDialogOpen(true);
  };

  const openEditDialog = async (member: Member) => {
    await fetchFormData();
    setSelectedMember(member);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      dob: member.dob || '',
      nationality: member.nationality || '',
      id_passport: member.id_passport || '',
      residential_address: member.residential_address || '',
      occupation: member.occupation || '',
      employer: member.employer || '',
      emergency_contact_name: member.emergency_contact_name || '',
      emergency_contact_relationship: member.emergency_contact_relationship || '',
      emergency_contact_phone: member.emergency_contact_phone || '',
      membership_type: member.member_type?.name || member.membership_type || '',
      playing_level: typeof member.playing_level === 'object' ? member.playing_level?.name || '' : member.playing_level || '',
      playing_level_id: member.playing_level_id?.toString() || '',
      playing_position_id: member.playing_position_id?.toString() || '',
      jersey_number: member.jersey_number?.toString() || '',
      medical_conditions: member.medical_conditions || '',
      allergies: member.allergies || '',
      insurance_provider: member.insurance_provider || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleMemberAction = (member: Member, action: 'approve' | 'reject' | 'suspend' | 'disable' | 'reactivate') => {
    setActionMember(member);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const executeMemberAction = async () => {
    if (!actionMember) return;
    try {
      setIsSubmitting(true);
      let reason = '';
      
      // Get reason for reject, suspend, or disable actions
      if (['reject', 'suspend', 'disable'].includes(actionType)) {
        reason = prompt(`Please provide a reason for ${actionType}ing this member:`);
        if (reason === null) return; // User canceled
      }

      switch (actionType) {
        case 'approve': 
          await membersApi.approve(actionMember.id); 
          break;
        case 'reject': 
          await membersApi.reject(actionMember.id, reason); 
          break;
        case 'suspend': 
          await membersApi.suspend(actionMember.id, reason); 
          break;
        case 'disable': 
          await membersApi.disable(actionMember.id, reason); 
          break;
        case 'reactivate': 
          await membersApi.reactivate(actionMember.id); 
          break;
      }
      
      toast({ 
        title: "Success", 
        description: `Member ${actionType}d successfully`,
        variant: "default"
      });
      setActionDialogOpen(false);
      setActionMember(null);
      fetchMembers();
    } catch (error: any) {
      console.error(`Error ${actionType}ing member:`, error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          `Failed to ${actionType} member`;
      toast({ 
        title: "Error", 
        description: errorMessage, 
        variant: "destructive" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'suspended': return <Badge className="bg-orange-100 text-orange-800">Suspended</Badge>;
      case 'disabled': return <Badge className="bg-red-100 text-red-800">Disabled</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const handleViewMember = async (member: Member) => {
    try {
      const response: any = await membersApi.getById(member.id);
      setSelectedMember(response.data || response);
    } catch (error) {
      console.error('Error fetching member details:', error);
      setSelectedMember(member);
    }
    setIsViewDialogOpen(true);
  };

  // Additional member management functions
  const fetchPendingMembers = async () => {
    try {
      const response = await membersApi.getAll({ status: 'pending' });
      const pendingData = Array.isArray(response) ? response : (response?.data || []);
      return pendingData;
    } catch (error) {
      console.error('Error fetching pending members:', error);
      return [];
    }
  };

  const fetchExpiringMembers = async (days: number = 30): Promise<Member[]> => {
    try {
      const response = await membersApi.getAll({ expiring_within: days });
      const expiringData = Array.isArray(response) ? response : (response?.data || []);
      return expiringData as Member[];
    } catch (error) {
      console.error('Error fetching expiring members:', error);
      return [];
    }
  };

  const fetchMembersByStatus = async (status: string) => {
    try {
      const response = await membersApi.getAll({ status });
      const statusData = Array.isArray(response) ? response : (response?.data || []);
      return statusData;
    } catch (error) {
      console.error(`Error fetching ${status} members:`, error);
      return [];
    }
  };

  // Quick action to show only pending members
  const showPendingMembers = () => {
    setStatusFilter('pending');
  };

  // Quick action to show only expiring members  
  const showExpiringMembers = async () => {
    try {
      const expiring = await fetchExpiringMembers(30);
      if (expiring.length > 0) {
        setStatusFilter('all');
        setMembers(expiring);
        toast({ 
          title: "Expiring Members", 
          description: `Found ${expiring.length} members expiring within 30 days`,
          variant: "default"
        });
      } else {
        toast({ 
          title: "No Expiring Members", 
          description: "No members found expiring within 30 days",
          variant: "default"
        });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
               description: "Failed to fetch expiring members", 
       variant: "destructive" 
     });
   }
 };

 // Photo management functions
 const handlePhotoUpload = async (file: File) => {
   if (!photoMember) return;
   try {
     setIsUploadingPhoto(true);
     await membersApi.uploadPhoto(photoMember.id, file);
     toast({ 
       title: "Success", 
       description: "Photo uploaded successfully", 
       variant: "default" 
     });
     setPhotoDialogOpen(false);
     fetchMembers();
   } catch (error: any) {
     console.error('Error uploading photo:', error);
     const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         'Failed to upload photo';
     toast({ 
       title: "Error", 
       description: errorMessage, 
       variant: "destructive" 
     });
   } finally {
     setIsUploadingPhoto(false);
   }
 };

 const openPhotoDialog = (member: Member) => {
   setPhotoMember(member);
   setPhotoDialogOpen(true);
 };

  useEffect(() => {
    fetchMembers();
  }, []);

  // Refresh members when search term or status filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchMembers();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter]);

  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm || 
      member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const MemberForm = ({ isEdit }: { isEdit: boolean }) => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold mb-3">Personal Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>First Name *</Label>
            <Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} placeholder="Enter first name" />
          </div>
          <div>
            <Label>Last Name *</Label>
            <Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} placeholder="Enter last name" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Enter email" />
          </div>
          <div>
            <Label>Phone *</Label>
            <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Enter phone" />
          </div>
          <div>
            <Label>Date of Birth</Label>
            <Input type="date" value={formData.dob} onChange={(e) => setFormData({...formData, dob: e.target.value})} />
          </div>
          <div>
            <Label>Nationality</Label>
            <Input value={formData.nationality} onChange={(e) => setFormData({...formData, nationality: e.target.value})} placeholder="Enter nationality" />
          </div>
        </div>
        <div className="mt-4">
          <Label>Residential Address</Label>
          <Textarea value={formData.residential_address} onChange={(e) => setFormData({...formData, residential_address: e.target.value})} placeholder="Enter address" rows={3} />
        </div>
      </div>

      <div>
        <h4 className="text-lg font-semibold mb-3">Membership Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Membership Type *</Label>
            <Select value={formData.membership_type} onValueChange={(value) => setFormData({...formData, membership_type: value})}>
              <SelectTrigger><SelectValue placeholder="Select membership type" /></SelectTrigger>
              <SelectContent>
                {memberTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>{type.name} - {type.monthly_fee} TZS/month</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Playing Level *</Label>
            <Select value={formData.playing_level} onValueChange={(value) => setFormData({...formData, playing_level: value})}>
              <SelectTrigger><SelectValue placeholder="Select playing level" /></SelectTrigger>
              <SelectContent>
                {playingLevels.map((level) => (
                  <SelectItem key={level.id} value={level.name}>{level.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Playing Position</Label>
            <Select value={formData.playing_position_id} onValueChange={(value) => setFormData({...formData, playing_position_id: value})}>
              <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
              <SelectContent>
                {playingPositions.map((position) => (
                  <SelectItem key={position.id} value={position.id.toString()}>{position.name} ({position.abbreviation})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Jersey Number</Label>
            <Input type="number" value={formData.jersey_number} onChange={(e) => setFormData({...formData, jersey_number: e.target.value})} placeholder="Enter jersey number" min="1" max="99" />
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-lg font-semibold mb-3">Emergency Contact</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Contact Name</Label>
            <Input value={formData.emergency_contact_name} onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})} placeholder="Enter contact name" />
          </div>
          <div>
            <Label>Relationship</Label>
            <Input value={formData.emergency_contact_relationship} onChange={(e) => setFormData({...formData, emergency_contact_relationship: e.target.value})} placeholder="e.g., Parent, Spouse" />
          </div>
          <div>
            <Label>Contact Phone</Label>
            <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})} placeholder="Enter contact phone" />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button variant="outline" onClick={() => isEdit ? setIsEditDialogOpen(false) : setIsAddDialogOpen(false)}>Cancel</Button>
        <Button onClick={isEdit ? handleEditMember : handleAddMember} disabled={isSubmitting || isFormDataLoading}>
          {isSubmitting ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update Member' : 'Add Member')}
        </Button>
      </div>
    </div>
  );

  const handleDownloadFormPDF = async () => {
    if (!selectedMember) return;

    const input = document.getElementById('member-form-preview-modal');
    if (!input) return;

    try {
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pageWidth;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight > pageHeight ? pageHeight : pdfHeight);
      pdf.save(`OG-membership-form-${selectedMember.first_name || 'member'}.pdf`);
      toast({
        title: "Success",
        description: "Member form PDF downloaded successfully!",
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate member form PDF.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Member Management</h1>
            <p className="text-gray-600 mt-2">Manage club members, profiles, and membership status</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setExportDialogOpen(true)} 
              variant="outline" 
              size="lg" 
              className="border-green-200 text-green-700 hover:bg-green-50"
            >
              <Download className="h-5 w-5 mr-2" />
              Export Data
            </Button>
            <Button onClick={openAddDialog} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-5 w-5 mr-2" />
              Add New Member
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Members</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-600">{stats.active}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={showPendingMembers}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Pending</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Suspended</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.suspended}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Ban className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Disabled</p>
                  <p className="text-3xl font-bold text-red-600">{stats.disabled}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Expiring Soon</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.expiring}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter Section */}
        <Card className="bg-white border-0 shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input 
                    placeholder="Search by name, email, or phone..." 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="pl-10 h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500" 
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={showPendingMembers}
                    className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
                  >
                    View Pending ({stats.pending})
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={showExpiringMembers}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    View Expiring ({stats.expiring})
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                      fetchMembers();
                    }}
                    className="text-gray-600 border-gray-200 hover:bg-gray-50"
                  >
                                         Clear Filters
                   </Button>
                   <Button 
                     variant="outline" 
                     size="sm" 
                     onClick={() => setExportDialogOpen(true)}
                     className="text-green-600 border-green-200 hover:bg-green-50"
                   >
                     <Download className="h-4 w-4 mr-1" />
                     Export
                   </Button>
                 </div>
                 <div className="text-sm text-gray-600">
                   Showing {filteredMembers.length} of {members.length} members
                 </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members Table */}
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">Members List</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">Loading members...</span>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-900 py-4">Member Info</TableHead>
                      <TableHead className="font-semibold text-gray-900">Contact Details</TableHead>
                      <TableHead className="font-semibold text-gray-900">Membership</TableHead>
                      <TableHead className="font-semibold text-gray-900">Basketball Info</TableHead>
                      <TableHead className="font-semibold text-gray-900">Status</TableHead>
                      <TableHead className="font-semibold text-gray-900">Join Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMembers.map((member, index) => (
                      <TableRow key={member.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <TableCell className="py-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {member.photo ? (
                                <img 
                                  src={member.photo} 
                                  alt={`${member.first_name} ${member.last_name}`} 
                                  className="w-12 h-12 rounded-full object-cover" 
                                />
                              ) : (
                                `${member.first_name?.charAt(0) || ''}${member.last_name?.charAt(0) || ''}`
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{member.first_name} {member.last_name}</p>
                              <p className="text-sm text-gray-500">ID: {member.id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">{member.email}</p>
                            <p className="text-sm text-gray-600">{member.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">
                              {member.member_type?.name || member.membership_type || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {member.membership_expires_at ? 
                                `Expires: ${new Date(member.membership_expires_at).toLocaleDateString()}` : 
                                'No expiry'
                              }
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">
                              {typeof member.playing_level === 'object' 
                                ? member.playing_level?.name 
                                : member.playing_level || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {member.playing_position?.name || 'No position'} 
                              {member.jersey_number && ` • #${member.jersey_number}`}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(member.status)}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-900">{new Date(member.created_at).toLocaleDateString()}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-gray-100">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleViewMember(member)} className="cursor-pointer">
                                <Eye className="h-4 w-4 mr-2" />View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(member)} className="cursor-pointer">
                                <Edit className="h-4 w-4 mr-2" />Edit Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openPhotoDialog(member)} className="cursor-pointer">
                                <CreditCard className="h-4 w-4 mr-2" />Upload Photo
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => {
                                  // Quick single member export
                                  const singleMemberData = prepareMemberDataForExport([member]);
                                  exportToCSV(singleMemberData, { 
                                    title: `Member Profile - ${member.first_name} ${member.last_name}`,
                                    filename: `member_${member.id}_${member.first_name}_${member.last_name}` 
                                  });
                                  toast({ title: "Exported", description: "Member data exported as CSV", variant: "default" });
                                }}
                                className="cursor-pointer"
                              >
                                <Download className="h-4 w-4 mr-2" />Quick Export CSV
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {member.status === 'pending' && (
                                <>
                                  <DropdownMenuItem onClick={() => handleMemberAction(member, 'approve')} className="cursor-pointer text-green-600">
                                    <CheckCircle className="h-4 w-4 mr-2" />Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleMemberAction(member, 'reject')} className="cursor-pointer text-red-600">
                                    <XCircle className="h-4 w-4 mr-2" />Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              {member.status === 'active' && (
                                <DropdownMenuItem onClick={() => handleMemberAction(member, 'suspend')} className="cursor-pointer text-orange-600">
                                  <Ban className="h-4 w-4 mr-2" />Suspend
                                </DropdownMenuItem>
                              )}
                              {(member.status === 'suspended' || member.status === 'disabled') && (
                                <DropdownMenuItem onClick={() => handleMemberAction(member, 'reactivate')} className="cursor-pointer text-green-600">
                                  <RotateCcw className="h-4 w-4 mr-2" />Reactivate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleMemberAction(member, 'disable')} className="cursor-pointer text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />Disable Account
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredMembers.length === 0 && !isLoading && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No members found</h3>
                    <p className="text-gray-500 mb-6">Get started by adding your first member to the system.</p>
                    <Button onClick={openAddDialog} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Member
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
            <DialogDescription>Fill in the member's information to add them to the club.</DialogDescription>
          </DialogHeader>
          <MemberForm isEdit={false} />
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
            <DialogDescription>Update the member's information.</DialogDescription>
          </DialogHeader>
          <MemberForm isEdit={true} />
        </DialogContent>
      </Dialog>

      {/* View Member Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[800px] p-6 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Member Details: {selectedMember?.first_name} {selectedMember?.last_name}</DialogTitle>
            <DialogDescription>
              View full registration details and download the form.
            </DialogDescription>
          </DialogHeader>
          <div id="member-form-preview-modal" className="bg-white p-4 rounded-lg shadow-inner">
            {selectedMember ? (
              <MemberFormTemplate data={selectedMember} showClubSection={false} />
            ) : (
              <p className="text-center text-gray-500">No member selected.</p>
                  )}
                </div>
          <div className="mt-4 flex justify-end">
            <Button 
              onClick={handleDownloadFormPDF} 
              className="bg-basketball-blue-600 hover:bg-basketball-blue-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
                    </Button>
                  </div>
        </DialogContent>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Member Photo</DialogTitle>
            <DialogDescription>
              Upload a photo for {photoMember?.first_name} {photoMember?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="photo-upload">Select Photo</Label>
              <Input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handlePhotoUpload(file);
                  }
                }}
                disabled={isUploadingPhoto}
                className="mt-2"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setPhotoDialogOpen(false)} disabled={isUploadingPhoto}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionType === 'approve' && 'Approve Member'}
              {actionType === 'reject' && 'Reject Member'}
              {actionType === 'suspend' && 'Suspend Member'}
              {actionType === 'disable' && 'Disable Member'}
              {actionType === 'reactivate' && 'Reactivate Member'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionMember && (
                <>
                  Are you sure you want to {actionType} {actionMember.first_name} {actionMember.last_name}?
                  {actionType === 'disable' && ' This action will remove their access to the system.'}
                  {actionType === 'reject' && ' This will decline their membership application.'}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeMemberAction}>
              {actionType === 'approve' && 'Approve'}
              {actionType === 'reject' && 'Reject'}
              {actionType === 'suspend' && 'Suspend'}
              {actionType === 'disable' && 'Disable'}
              {actionType === 'reactivate' && 'Reactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        data={filteredMembers}
        stats={stats}
        title="Export Member Data"
      />
    </div>
  );
};

export default AdminMembers; 